<template>
	<div>
		<internal-submenu />
		<core-layout-content full>
			<!-- Headline -->
			<core-layout-headline-and-actions>
				<template slot="headline">{{ $t("subscriptions") }}</template>
				<template slot="actions">
					<j-button
						v-can:and="['writeSubscriptions']"
						@click="$modal.show('createSubscription')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("create_subscription") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Subscriptions -->
			<j-card
				v-for="(subscription, i) in computedSubscriptions"
				:key="i"
				class="mb4"
			>
				<j-expendable-box class="bb1 bc7" bodyClass="p3">
					<div slot="header" class="p4">
						<j-tag
							:label="subscription.isCancelled ? $t('cancelled') : $t('active')"
							:variant="subscription.isCancelled ? 'error' : 'success'"
						/>
						<span class="ml2 fs3 fw7 c6">{{ subscription.createdAt }}</span>
					</div>

					<div slot="body" class="p4">
						<!-- Details -->
						<div class="flex xs-12 xs-between xs-middle">
							<div class="fs4 lh125 fw7 c1 mb2">{{ $t("details") }}</div>
							<j-button
								type="tertiary"
								@click="handleUpdateSubscription(subscription.id)"
							>
								<crm-icon name="edit" size="normal" color="c2" />
								{{ $t("update_subscription") }}
							</j-button>
						</div>
						<j-card class="mb6">
							<!-- Is Cancelled -->
							<div class="flex justify-between p3 bc7 bb1">
								<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">{{ $t("status") }}</div>
								<div class="flex xs-4 xs-left xs-middle lh125">
									<j-tag
										:label="
											subscription.isCancelled ? $t('cancelled') : $t('active')
										"
										:variant="subscription.isCancelled ? 'error' : 'success'"
									/>
								</div>
							</div>
							<div class="flex justify-between p3 bc7 bb1">
								<div
									class="flex xs-6 xs-left xs-middle fw7 c6 lh125"
								>{{ $t("payment_method") }}</div>
								<div
									class="flex xs-4 xs-left xs-middle lh125"
								>{{ subscription.paymentMethod }}</div>
							</div>
							<!-- Start Date-->
							<div class="flex justify-between p3 bc7 bb1">
								<div
									class="flex xs-6 xs-left xs-middle fw7 c6 lh125"
								>{{ $t("start_date") }}</div>
								<div
									class="flex xs-4 xs-left xs-middle lh125"
								>{{ subscription.startDate }}</div>
							</div>
							<!-- Interval -->
							<div class="flex justify-between p3 bc7 bb1">
								<div
									class="flex xs-6 xs-left xs-middle fw7 c6 lh125"
								>{{ $t("interval") }}</div>
								<div
									class="flex xs-4 xs-left xs-middle lh125"
								>{{ subscription.interval }}</div>
							</div>
							<!-- Created At -->
							<div class="flex justify-between p3 bc7 bb1">
								<div
									class="flex xs-6 xs-left xs-middle fw7 c6 lh125"
								>{{ $t("created_at") }}</div>
								<div
									class="flex xs-4 xs-left xs-middle lh125"
								>{{ subscription.createdAt }}</div>
							</div>
						</j-card>

						<!-- Plans -->
						<div class="flex xs-12 xs-between xs-middle">
							<div class="fs4 lh125 fw7 c1 mb2">{{ $t("plans") }}</div>
							<j-button type="tertiary" @click="handleAddPlan(subscription.id)">
								<crm-icon name="add" size="normal" color="c2" />
								{{ $t("add_plan") }}
							</j-button>
						</div>

						<j-card class="mb6">
							<j-data-table
								:header="plansHeader"
								:list="subscription.plans"
								:sortable="false"
								:isLoading="isLoading"
								@sort="onSort($event)"
							>
								<template slot="list" slot-scope="{ item }">
									<td v-for="(column, i) in plansHeader" :key="i">
										<template>
											<!-- Others -->
											<template>{{ item[column.field] }}</template>

											<!-- Actions Field -->
											<template v-if="column.field === 'actions'">
												<div class="action">
													<!-- Delete -->
													<crm-icon
														v-if="subscription.plans.length > 1"
														name="delete"
														size="normal"
														color="cError"
														@click.native="deletePlan(subscription.id, item.id)"
														v-can="'writePermissionGroups'"
													/>
												</div>
											</template>
										</template>
									</td>
								</template>
								<template slot="empty">{{ $t("errors.no_data") }}</template>
							</j-data-table>
						</j-card>

						<!-- Footer -->
						<div class="flex xs-12 xs-right">
							<j-button
								type="secondary"
								size="small"
								class="mr2"
								@click="handleCancel(subscription.id)"
								:isLoading="isLoading"
							>{{ $t("cancel") }}</j-button>
							<j-button
								type="error"
								size="small"
								@click="handleDelete(subscription.id)"
							>{{ $t("delete") }}</j-button>
						</div>
					</div>
				</j-expendable-box>
			</j-card>

			<!-- Create Modal -->
			<core-modal name="createSubscription" :headline="$t('create_subscription')">
				<div class="p3">
					<core-form-input
						class="mb4"
						:label="$t('payment_method')"
						:options="computedPaymentMethods"
						componentName="j-base-select"
						v-model="paymentMethodId"
					/>
					<core-form-input
						class="mb4"
						:label="$t('plan')"
						:options="computedPlans"
						componentName="j-base-select"
						v-model="planId"
					/>
					<core-form-input
						class="mb4"
						:label="$t('start_date')"
						componentName="j-base-date-picker-simple"
						v-model="startDate"
					/>
					<!-- <core-form-input
						class="mb4"
						:label="$t('end_date')"
						componentName="j-base-date-picker-simple"
						v-model="endDate"
					/>-->
					<core-form-input
						class="mb4"
						:label="$t('interval')"
						:options="computedIntervals"
						componentName="j-base-select"
						v-model="interval"
					/>
					<!-- <core-form-input
						class="mb4"
						:label="$t('interval_count')"
						componentName="j-base-input-number"
						v-model="intervalCount"
					/>-->
					<j-button size="small" @click="createSubscription" :isLoading="isLoading">
						{{
						$t("create")
						}}
					</j-button>
				</div>
			</core-modal>

			<!-- Delete Modal -->
			<j-dialog
				:title="$t('delete_subscription')"
				type="delete"
				@confirm="deleteSubscription"
			>
				{{
				$t("errors.are_you_sure_you_want_to_delete_subscription")
				}}
			</j-dialog>

			<!-- Add Offer/Plan modal -->
			<core-modal name="addPlan" :headline="$t('add_plan')">
				<div class="p3">
					<core-form-input
						class="mb4"
						:label="$t('plan')"
						:options="computedPlans"
						componentName="j-base-select"
						v-model="planId"
					/>
					<j-button
						size="small"
						@click="addPlan"
						:isLoading="isLoading"
					>{{ $t("add") }}</j-button>
				</div>
			</core-modal>

			<!-- Update Subscription Modal -->
			<core-modal name="updateSubscription" :headline="$t('update_subscription')">
				<div class="p3">
					<core-form-input
						class="mb4"
						:label="$t('payment_method')"
						:options="computedPaymentMethods"
						componentName="j-base-select"
						v-model="paymentMethodId"
					/>
					<!-- <core-form-input
						class="mb4"
						:label="$t('end_date')"
						componentName="j-base-date-picker-simple"
						v-model="endDate"
					/>-->
					<j-button size="small" @click="updateSubscription">
						{{
						$t("update")
						}}
					</j-button>
				</div>
			</core-modal>
		</core-layout-content>
	</div>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import JButton from "@/components/JButton"
import InternalSubmenu from "./InternalSubmenu"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JDataTable from "@/components/JDataTable/"
import JCard from "@/components/JCard/"
import CrmIcon from "@/components/CrmIcon/"
import JExpendableBox from "@/components/JExpendableBox/"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JDialog from "@/components/JDialog/"
import JTag from "@/components/JTag/"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

export default {
	components: {
		CoreLayoutContent,
		JButton,
		InternalSubmenu,
		CoreLayoutHeadlineAndActions,
		JDataTable,
		JCard,
		CrmIcon,
		JExpendableBox,
		CoreModal,
		CoreFormInput,
		JDialog,
		JTag
	},

	data() {
		return {
			isLoading: false,
			subscriptions: [],

			selectedId: null,
			plans: [],
			paymentMethods: [], // payment method of this internal

			// create subscription fields
			paymentMethodId: null,
			planId: null,
			startDate: null,
			endDate: null,
			interval: null,
			intervalCount: null
		}
	},

	methods: {
		async fetchSubscriptions() {
			this.isLoading = true
			const query = gql`
				query internal($where: internalWhereInput) {
					internals(where: $where) {
						totalCount
						list {
							id
							subscriptions {
								id
								startDate
								endDate
								interval
								isCancelled
								createdAt
								offers {
									id
									product {
										productId {
											plan
											type
											version
										}
									}
								}
								paymentMethod {
									id
									... on cardPaymentMethod {
										brand
										expMonth
										expYear
										last4
										createdAt
									}
								}
							}
							paymentMethods {
								id
								... on cardPaymentMethod {
									brand
									expMonth
									expYear
									last4
									createdAt
								}
							}
						}
					}
				}
			`

			const variables = {
				where: { id: this.$route.params.id }
			}
			const res = await api({ query, variables })
			this.isLoading = false

			// Assign
			this.subscriptions = res?.data?.internals?.list[0]?.subscriptions
			this.paymentMethods = res?.data?.internals?.list[0]?.paymentMethods
		},

		async fetchPlans() {
			this.isLoading = true
			const query = gql`
				query plans {
					plans {
						description
						id {
							plan
							type
							version
						}
						name
						price {
							formattedPrice
							currencyCode
							amount
						}
					}
				}
			`

			const variables = {}

			const res = await api({ query, variables })
			this.isLoading = false

			// Assign
			this.plans = res?.data?.plans
		},

		async createSubscription() {
			this.isLoading = true
			const query = gql`
				mutation createSubscription(
					$providerConfigId: String!
					$entityId: String!
					$paymentMethodId: String!
					$input: createSubscriptionInput!
				) {
					createSubscription(
						providerConfigId: $providerConfigId
						entityId: $entityId
						paymentMethodId: $paymentMethodId
						input: $input
					) {
						errors
						status
					}
				}
			`

			const selectedPlan = this.plans?.find(
				item => item?.id?.plan === this.planId
			)
			const offerInput = {
				productId: selectedPlan?.id,
				currencyCode: selectedPlan?.price?.currencyCode,
				amount: selectedPlan?.price?.amount
			}

			const variables = {
				providerConfigId: "5e8daed004c4cf8f70c0ccd8", // TODO: hardcoded
				entityId: this.$route.params.id,
				paymentMethodId: this.paymentMethodId,
				input: {
					offers: [offerInput],
					startDate: this.startDate,
					// endDate: this.endDate,
					interval: this.interval
					// intervalCount: this.intervalCount
				}
			}

			const res = await api({ query, variables })

			if (res?.data?.createSubscription?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.createSubscription?.errors ||
							res?.data?.createSubscription?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.isLoading = false
			this.$modal.hide("createSubscription")
			this.fetchSubscriptions()

			// clear form
			this.paymentMethodId = null
			this.planId = null
			this.startDate = null
			this.endDate = null
			this.interval = null
		},

		handleDelete(id) {
			this.selectedId = id
			this.$modal.show("j-dialog")
		},

		async deleteSubscription() {
			this.isLoading = true
			const query = gql`
				mutation deleteSubscription($id: String!) {
					deleteSubscription(id: $id) {
						errors
						status
					}
				}
			`

			const variables = { id: this.selectedId }

			const res = await api({ query, variables })

			if (res?.data?.deleteSubscription?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deleteSubscription?.errors ||
							res?.data?.deleteSubscription?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.isLoading = false
			this.selectedId = null
			this.$modal.hide("j-dialog")
			this.fetchSubscriptions()
		},

		async handleCancel(id) {
			this.selectedId = id
			await this.cancelSubscription()
		},

		async cancelSubscription() {
			this.isLoading = true
			const query = gql`
				mutation cancelSubscription($id: String!) {
					cancelSubscription(subscriptionId: $id) {
						errors
						status
					}
				}
			`

			const variables = { id: this.selectedId }

			const res = await api({ query, variables })

			if (res?.data?.cancelSubscription?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.cancelSubscription?.errors ||
							res?.data?.cancelSubscription?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.isLoading = false
			this.selectedId = null
			this.fetchSubscriptions()
		},

		handleAddPlan(id) {
			this.selectedId = id
			this.$modal.show("addPlan")
		},

		async addPlan() {
			this.isLoading = true
			const query = gql`
				mutation addOfferToSubscription(
					$subscriptionId: String!
					$input: addOfferInput!
				) {
					addOfferToSubscription(
						subscriptionId: $subscriptionId
						input: $input
					) {
						errors
						status
					}
				}
			`

			const selectedPlan = this.plans?.find(
				item => item?.id?.plan === this.planId
			)
			const offerInput = {
				productId: selectedPlan?.id,
				currencyCode: selectedPlan?.price?.currencyCode,
				amount: selectedPlan?.price?.amount
			}

			const variables = {
				subscriptionId: this.selectedId,
				input: offerInput
			}

			const res = await api({ query, variables })

			if (res?.data?.addOfferToSubscription?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addOfferToSubscription?.errors ||
							res?.data?.addOfferToSubscription?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.isLoading = false
			this.$modal.hide("addPlan")
			this.fetchSubscriptions()

			// clear form
			this.selectedId = null
			this.planId = null
		},

		async deletePlan(subscriptionId, offerId) {
			this.isLoading = true
			const query = gql`
				mutation removeOfferFromSubscription(
					$subscriptionId: String!
					$offerId: String!
				) {
					removeOfferFromSubscription(
						subscriptionId: $subscriptionId
						offerId: $offerId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				subscriptionId: subscriptionId,
				offerId: offerId
			}

			const res = await api({ query, variables })

			if (res?.data?.removeOfferFromSubscription?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeOfferFromSubscription?.errors ||
							res?.data?.removeOfferFromSubscription?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.isLoading = false
			this.fetchSubscriptions()
		},

		handleUpdateSubscription(id) {
			this.selectedId = id
			this.$modal.show("updateSubscription")
		},

		async updateSubscription() {
			this.isLoading = true
			const query = gql`
				mutation updateSubscription(
					$subscriptionId: String!
					$input: updateSubscriptionInput!
				) {
					updateSubscription(subscriptionId: $subscriptionId, input: $input) {
						errors
						status
					}
				}
			`

			const variables = {
				subscriptionId: this.selectedId,
				input: {
					// endDate: this.endDate,
					paymentMethodId: this.paymentMethodId
				}
			}

			const res = await api({ query, variables })

			if (res?.data?.updateSubscription?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateSubscription?.errors ||
							res?.data?.updateSubscription?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}

			this.isLoading = false
			this.$modal.hide("updateSubscription")
			this.fetchSubscriptions()

			// clear form
			this.selectedId = null
			this.paymentMethodId = null
			this.endDate = null
		}
	},

	computed: {
		// Header of table
		plansHeader() {
			return [
				{
					field: "plan",
					text: this.$t("plan")
				},
				{
					field: "type",
					text: this.$t("type")
				},
				{
					text: this.$t("version"),
					field: "version"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			]
		},

		computedSubscriptions() {
			return this.subscriptions?.map(item => ({
				id: item?.id,
				plans: item?.offers?.map(plan => ({
					id: plan?.id,
					plan: plan?.product?.productId.plan || "---",
					type: plan?.product?.productId.type || "---",
					version: plan?.product?.productId.version || "---"
				})),
				paymentMethod: item?.paymentMethod
					? `${item?.paymentMethod?.brand} ******${item?.paymentMethod?.last4}`
					: "---",
				isCancelled: item?.isCancelled,
				startDate: dayjs(item.startDate).format(
					this.$store.state.settings.dateTimeFormat
				),
				interval: item?.interval || "---",
				createdAt: dayjs(item.createdAt).format(
					this.$store.state.settings.dateTimeFormat
				)
			}))
		},

		computedPlans() {
			return this.plans?.map(item => ({
				name: item.name,
				value: item?.id?.plan
			}))
		},

		computedPaymentMethods() {
			return this.paymentMethods?.map(item => ({
				name: `${item.brand} - ${item.last4}`,
				value: item.id
			}))
		},

		computedIntervals() {
			return [
				{ name: this.$t("day"), value: "DAY" },
				{ name: this.$t("week"), value: "WEEK" },
				{ name: this.$t("month"), value: "MONTH" },
				{ name: this.$t("year"), value: "YEAR" }
			]
		}
	},

	created() {
		this.fetchSubscriptions()
		this.fetchPlans()
	}
}
</script>
